import { Link } from 'react-router-dom';
import ArticleLayout from '../../components/ArticleLayout';
import RadiatornoegleFigure from '../../components/figures/RadiatornoegleFigure';

const linkClass = 'text-[#B5531E] hover:underline underline-offset-4';

export default function Radiatornoegle() {
  return (
    <ArticleLayout
      title="Radiatornøgle: Hvilken nøgle passer til udluftning af radiatoren?"
      metaTitle="Radiatornøgle: Hvilken nøgle passer til udluftning?"
      metaDescription="Hvilken radiatornøgle passer til luftskruen? Se de typiske luftskruer, hvilket værktøj hver kræver, hvor du får en nøgle, og hvad du gør uden."
      canonicalPath="/radiatornoegle"
      datePublished="2026-11-03"
      dateModified="2026-11-03"
      introLead="En radiatornøgle, også kaldet en udluftningsnøgle, er det lille værktøj, du bruger til at åbne luftskruen øverst på radiatoren, når der skal lukkes luft ud."
      introAnswer="Det almindelige værktøj er en radiatornøgle, der passer til den firkantede eller trekantede kærv på luftskruen. Har luftskruen en lige kærv, kan den åbnes med en flad skruetrækker i stedet. Luftskruen sidder typisk øverst i den modsatte ende af termostaten, og en radiatornøgle kan som regel købes i et byggemarked."
      tableOfContents={[
        { id: 'tjekliste', title: '1. Tjekliste: Find den nøgle, der passer' },
        { id: 'typer', title: '2. Luftskruer og det værktøj, de kræver' },
        { id: 'koeb', title: '3. Hvor får man en radiatornøgle?' },
        { id: 'uden-noegle', title: '4. Når du ikke har en nøgle' },
        { id: 'brug', title: '5. Brug nøglen roligt' },
        { id: 'faq', title: '6. Ofte stillede spørgsmål' },
      ]}
      checklistTitle="Tjekliste: Find den nøgle, der passer til din luftskrue"
      checklist={[
        {
          title: 'Find luftskruen på radiatoren',
          description: 'Den sidder typisk øverst i den modsatte ende af termostaten og ligner en lille metalskrue eller en studs, der stikker ud.',
        },
        {
          title: 'Se på luftskruens hoved',
          description: 'En firkantet eller trekantet tap kræver en radiatornøgle, mens en lige kærv kan åbnes med en flad skruetrækker.',
        },
        {
          title: 'Køb en radiatornøgle, hvis du ikke har en',
          description: 'Radiatornøgler kan ofte købes i det lokale byggemarked.',
        },
        {
          title: 'Sæt nøglen helt på, før du drejer',
          description: 'Drej først, når nøglen sidder helt inde over tappen og ikke vipper.',
        },
        {
          title: 'Stop, hvis luftskruen ikke vil dreje',
          description: 'Brug ikke mere kraft, men læs om fastsiddende luftskruer, før du går videre.',
        },
      ]}
      diagram={<RadiatornoegleFigure />}
      sections={[
        {
          id: 'typer',
          title: 'Luftskruer og det værktøj, de kræver',
          content: (
            <>
              <p>
                Det er luftskruens hoved, der afgør, hvilken nøgle til udluftning af radiatoren du skal bruge. Se på hovedet, før du køber noget.
              </p>
              <p>
                <strong>Firkantet eller trekantet tap:</strong> Her skal du bruge en radiatornøgle. Nøglen passer ind over tappen, så du kan dreje luftskruen.
              </p>
              <p>
                <strong>Lige kærv:</strong> Er luftskruen udformet som en skrue med en kærv, kan den åbnes med en almindelig flad skruetrækker.
              </p>
              <p>
                <strong>Andre udformninger:</strong> Der findes også luftskruer, der skal åbnes med en papegøjetang. Værktøjet afhænger af radiatorens ventiltype, så brug det værktøj, der passer til netop din luftskrue.
              </p>
              <p>
                Har du flere radiatorer, så se på luftskruen på hver af dem. Sidder der forskellige typer i huset, kan du få brug for mere end ét værktøj.
              </p>
            </>
          ),
        },
        {
          id: 'koeb',
          title: 'Hvor får man en radiatornøgle?',
          content: (
            <>
              <p>
                En radiatornøgle kan ofte købes i det lokale byggemarked, sammen med andet småværktøj til huset. Det samme gælder en papegøjetang.
              </p>
              <p>
                Tag gerne et billede af luftskruen med, før du går ud og køber. Så kan du sammenligne formen på tappen med nøglerne i butikken og undgå at købe en, der ikke passer.
              </p>
            </>
          ),
        },
        {
          id: 'uden-noegle',
          title: 'Når du ikke har en radiatornøgle',
          content: (
            <>
              <p>
                Har du ingen radiatornøgle ved hånden, kan en flad skruetrækker kun bruges, hvis luftskruen har en lige kærv. På en firkantet eller trekantet tap har skruetrækkeren ikke noget at gribe i.
              </p>
              <p>
                Den sikre løsning er at vente, til du har en nøgle, der passer til tappen. Improviser ikke med værktøj, som skruen ikke er lavet til.
              </p>
              <p>
                Sidder luftskruen fast, eller er tappen allerede beskadiget, så læs{' '}
                <Link to="/radiator-luftskrue-sidder-fast" className={linkClass}>
                  hvad du må gøre, når luftskruen sidder fast
                </Link>
                , før du prøver noget andet.
              </p>
            </>
          ),
        },
        {
          id: 'brug',
          title: 'Brug nøglen roligt',
          content: (
            <>
              <p>
                Selve udluftningen er beskrevet trin for trin i{' '}
                <Link to="/udluftning-af-radiator" className={linkClass}>
                  guiden til udluftning af radiatoren
                </Link>
                . Her er kun det, der handler om nøglen.
              </p>
              <p>
                Hold en skål og en klud under luftskruen. Drej nøglen langsomt mod uret, og åbn ikke skruen helt. Det tager normalt en halv omgang eller mindre, før du hører luften hvæse. Når der kommer vand i stedet for luft, lukker du skruen igen.
              </p>
              <p>
                Pas på hænderne. Både luften og vandet, der kommer ud, kan være varmt.
              </p>
              <p>
                Kommer der ikke luft ud, når skruen er løsnet, skyldes problemet med varmen ikke luft i radiatoren. Så er det{' '}
                <Link to="/radiator-varmer-ikke" className={linkClass}>
                  tjeklisten for en radiator, der ikke varmer
                </Link>
                , du skal bruge.
              </p>
            </>
          ),
        },
      ]}
      proCallout={{
        title: 'Hvornår skal du stoppe og ringe til en fagmand?',
        content: (
          <>
            <p>
              Vil luftskruen ikke dreje med en nøgle, der passer, eller er den beskadiget, så stop. Udskiftning af en luftskrue og andet arbejde, der åbner for anlæggets vand, skal udføres af en autoriseret VVS-installatør.
            </p>
            <p>
              Efter udluftning kan trykket i varmeanlægget være faldet. Læs om{' '}
              <Link to="/radiator-tryk" className={linkClass}>
                trykket i varmeanlægget
              </Link>
              , og lad en autoriseret VVS-installatør tage sig af påfyldningen, hvis du er i tvivl.
            </p>
          </>
        ),
      }}
      faq={[
        {
          q: 'Er en radiatornøgle og en udluftningsnøgle det samme?',
          a: 'Ja. Det er to navne for den samme nøgle, som passer til den firkantede eller trekantede kærv på radiatorens luftskrue.',
        },
        {
          q: 'Hvor sidder luftskruen på radiatoren?',
          a: 'Typisk øverst i den modsatte ende af termostaten. Den ligner en lille metalskrue eller en studs, der stikker ud.',
        },
        {
          q: 'Kan jeg lufte radiatoren ud med en skruetrækker?',
          a: 'Kun hvis luftskruen har en lige kærv. Har den en firkantet eller trekantet tap, skal du bruge en radiatornøgle.',
        },
        {
          q: 'Hvad betyder det, hvis der ikke kommer luft ud?',
          a: 'Hvis der ikke kommer luft ud, når skruen er løsnet, skyldes problemet med varmen ikke luft i radiatoren. Så skal årsagen findes et andet sted.',
        },
      ]}
      internalLinks={[
        { to: '/udluftning-af-radiator', text: 'Sådan lufter du en radiator ud', desc: 'hele udluftningen trin for trin' },
        { to: '/radiator-luftskrue-sidder-fast', text: 'Luftskruen sidder fast eller er rundet', desc: 'hvad du må prøve, og hvornår du skal stoppe' },
        { to: '/radiator-varmer-ikke-efter-udluftning', text: 'Radiatoren varmer ikke efter udluftning', desc: 'de næste ting at tjekke' },
        { to: '/radiator-tryk', text: 'Trykket i varmeanlægget er faldet', desc: 'tjek trykket efter udluftning' },
        { to: '/radiator-varmer-ikke', text: 'Radiatoren varmer ikke', desc: 'den samlede tjekliste' },
      ]}
      sources={[
        { url: 'https://www.viessmann.dk/da/viden/raad-og-koebsvejledning/udluftning-af-radiator.html', label: 'Udluftning af radiator trin-for-trin guide | Viessmann DK' },
        { url: 'https://nrgi.dk/privat/energivenlig/udluftning-af-radiator-saadan-lukker-du-luft-ud-af-dine-radiatorer/', label: 'Udluftning af radiator: Følg ekspertens simple guide | NRGi' },
      ]}
    />
  );
}
